import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import dayjs from 'dayjs';
import _ from 'lodash';
import { svg } from '../assets/svg';
import { components } from '../components';
import constants from '../helpers/constants';
import dateHelper from '../helpers/dateHelper';
import Enums from '../helpers/enums';
import utils from '../helpers/utils';
import { hooks } from '../hooks';
import { Routes } from '../routes';
import ApiService from '../service/ApiService';
import { RootState } from '../store';
import { clearCart } from '../store/slices/cartSlice';
import { CartType } from '../types';
import { showSuccessToast } from '../components/Toast';
import { setCurrentScreen } from '../store/slices/screenSlice';
import { setIsPointsUpdate } from '../store/slices/lookupSlice';
import visaCardImg from "../assets/icons/visa.png";
import masterCardImg from "../assets/icons/master.png";

interface Errors {
  cart: any;
  date: any;
  site: any;
  breakfast: any;
  items: any[];
}

export const Checkout: React.FC = () => {
  const dispatch = hooks.useDispatch();
  const navigate = hooks.useNavigate();
  const [opacity, setOpacity] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [isSwitchActive, setIsSwitchActive] = useState<boolean>(false);
  const [pointsToBeRedeem, setPointsToBeRedeem] = useState<any>(null);

  const initialErrorState = {
    cart: '',
    date: '',
    site: '',
    breakfast: '',
    items: [],
  };
  const [errors, setErrors] = useState<Errors>(initialErrorState);
  const [promiseDatetime, setPromiseDateTime] = useState<any>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  const cartItems = useSelector((state: RootState) => state.cartReducer.cartItems);
  const paymentMethod = useSelector((state: RootState) => state.appReducer.paymentMethod);
  const selectedSiteId = useSelector((state: RootState) => state.selectedSiteReducer.selectedSiteId);
  const allSites = useSelector((state: RootState) => state.screenReducer.sites);
  const config = useSelector((state: RootState) => state.appReducer.config);
  const { user, token } = useSelector((state: RootState) => state.authReducer.auth);
  const totalUserPoints = useSelector((state: RootState) => state.appReducer.userPoints);
  const orderMode = useSelector((state: RootState) => state.appReducer.orderMode);

  const selectedSite = utils.getSelectedSite(selectedSiteId, allSites);
  const latitude = 0;
  const longitude = 0;

  hooks.useScrollToTop();
  hooks.useOpacity(setOpacity);
  hooks.useThemeColor('#F6F9F9', '#F6F9F9', dispatch);

  const totalCheckOutPrice = useMemo(() => {
    let totalPrice = 0;
    totalPrice += _.sumBy(cartItems, (item: any) => {
      if (item.type === Enums.comboType.quickCombo) {
        return (
          (item.price + item.totalCutomizedItemPrice + item.swappedPrice) *
          item.quantity
        );
      } else {
        let itemPrice =
          (item.price + item.totalCutomizedItemPrice) * item.quantity;
        if (item.options && item.options.length > 0) {
          itemPrice += item.options[0].price * item.quantity;
        }
        return itemPrice;
      }
    });
    return Number(totalPrice.toFixed(2));
  }, [cartItems]);

  useEffect(() => {
    setPointsToBeRedeem(totalCheckOutPrice * 2000);
  }, [cartItems, totalCheckOutPrice, orderMode]);

  useEffect(() => {
    if (totalUserPoints <= pointsToBeRedeem) {
      setIsSwitchActive(false);
    }
  }, [pointsToBeRedeem]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const setOrderDateTime = () => {
    const prepTime = selectedSite?.preparationTime ?? constants.orderPrepTime;
    const prepTimeFromNow = new Date(new Date().getTime() + prepTime * 60000);
    const openingTime = dateHelper.getMinutes(selectedSite?.openingTime);
    let orderDateTime = promiseDatetime ? promiseDatetime : prepTimeFromNow;
    orderDateTime = dayjs(orderDateTime);
    const promiseTime = orderDateTime.hour() * 60 + orderDateTime.minute();
    if (openingTime != null && promiseTime < openingTime) {
      orderDateTime = orderDateTime.add(1, 'days');
    }
    return orderDateTime;
  };

  const isBreakFastItemsInCart = () => {
    const isBreakfastClosed = dateHelper.isBreakfastClosed(
      selectedSite?.timeZoneName,
      new Date()
    );
    if (isBreakfastClosed) {
      return _.some(cartItems, (item) => utils.hasBreakfastItems(item));
    }
  };

  const onCheckLogin = () => {
    if (!_.isNil(token)) {
      navigate(Routes.PaymentMethods, { state: { isDisable: true } });
    } else {
      dispatch(setCurrentScreen(Routes.Checkout));
      navigate(Routes.SignIn);
    }
  };

  const onPressOrderValidate = async () => {
    setErrors(initialErrorState);
    let valid = true;
    const freeItem = cartItems.find((item) => item.isFree);
    let orderTime;
    if (orderMode === Enums.orderMode.Delivery) {
      orderTime = new Date(
        new Date().getTime() + constants.orderPrepTime * 60000
      );
    } else {
      orderTime = setOrderDateTime();
    }

    if (isBreakFastItemsInCart()) {
      setErrors((prev) => ({
        ...prev,
        breakfast:
          'Breakfast items in your cart are not available at your requested time.',
      }));
      valid = false;
    }

    if (
      orderMode === Enums.orderMode.Delivery &&
      totalCheckOutPrice < config.minDelivery
    ) {
      setErrors((prev) => ({
        ...prev,
        cart: {
          cart: `Sorry, we can't deliver for orders under ${utils.formatCurrency(
            config.minDelivery
          )}.`,
        },
      }));
      valid = false;
    }

    if (
      freeItem &&
      orderMode === Enums.orderMode.Delivery &&
      totalCheckOutPrice < config.minFreeItemDelivery
    ) {
      setErrors((prev) => ({
        ...prev,
        cart: {
          cart: `Sorry, we can't deliver for orders under ${utils.formatCurrency(
            config.minFreeItemDelivery
          )} containing a free item.`,
        },
      }));
      valid = false;
    }

    if (
      freeItem &&
      _.size(cartItems) === 1 &&
      orderMode === Enums.orderMode.Delivery
    ) {
      setErrors((prev) => ({
        ...prev,
        cart: {
          cart: "Sorry, we can't process orders that contain only free item.",
        },
      }));
      valid = false;
    }

    if (user && user.isGuest && orderMode === Enums.orderMode.Delivery) {
      setErrors((prev) => ({
        ...prev,
        cart: { cart: 'Delivery is not available for guest user.' },
      }));
      valid = false;
    }

    if (_.isEmpty(paymentMethod) && !isSwitchActive) {
      setErrors((prev) => ({
        ...prev,
        cart: { cart: 'Please select payment method.' },
      }));
      valid = false;
    }

    if (!valid) {
      return;
    }

    setLoading(true);
    await onPressCheckout();
  };

  const onPressCheckout = async () => {
    const items: any[] = [];
    const options: any[] = [];

    _.map(cartItems, (item: any) => {
      if (item.promoId && item.promoId > 0) {
        item?.items.map((comboItem: any) => {
          const cartItem = createCartItem(item, comboItem, null);
          items.push(cartItem);
        });
      } else {
        const cartItem = createCartItem(item, null, options);
        items.push(cartItem);
      }
    });

    const cartPayload: CartType = {
      siteId: selectedSiteId,
      orderMode: orderMode,
      paymentMethodId: isSwitchActive || totalCheckOutPrice === 0 ? constants.userPointsMethodId : paymentMethod.paymentMethodId,
      payType: paymentMethod?.payType,
      orderSource: 'Mobile',
      charges: {
        tax: totalCheckOutPrice / constants.gstAmount,
        discount: 0,
        total: totalCheckOutPrice,
      },
      items,
    };

    if (orderMode === Enums.orderMode.Delivery) {
      cartPayload.promiseDateTime = new Date();
    } else {
      cartPayload.promiseDateTime = setOrderDateTime();
    }

    await onSaveOrder(cartPayload);
  };

  const createCartItem = (item: any, comboItem: any, options: any) => {
    const cartItem: CartType = {
      _id: item._id,
      menuItemId: item.menuItemId,
      isFree: item.isFree ?? false,
      posItemId: item.posItemId,
      name: item.name,
      quantity: item.quantity,
      isOptionItem: item.isOption,
      orderDateTime: item.orderDateTime,
      options: item.options,
    };
    let customizations = null;
    if (item.type === Enums.comboType.menuItem && item.customizations) {
      customizations = item.customizations;
    } else if (
      item.type === Enums.comboType.quickCombo &&
      comboItem?.customizations
    ) {
      customizations = comboItem.customizations;
    }

    if (item.promoId && item.promoId > 0 && comboItem) {
      cartItem.promoId = comboItem.promoId;
      cartItem.menuItemId = comboItem.menuItemId;
      cartItem.componentId = comboItem.componentId;
      cartItem.itemGroupId = comboItem.itemGroupId;
      cartItem.posItemId = comboItem.posItemId;
      cartItem.salesItemId = comboItem.salesItemId;
      cartItem.orderDateTime = item.orderDateTime;
      cartItem.comboLevelId = item.comboLevelId;
      cartItem.name =
        comboItem.name +
        (comboItem.swapPrice > 0
          ? ` +(${utils.formatCurrency(comboItem.swapPrice)})`
          : '');
      cartItem.price =
        comboItem.componentId === 1
          ? (item.price + item.totalCutomizedItemPrice + item.swappedPrice) *
          item.quantity
          : 0;
    } else {
      cartItem.price =
        (item.price + item.totalCutomizedItemPrice) * item.quantity;
      if (item.options && item.options.length > 0) {
        cartItem.price += item.options[0].price * item.quantity;
      }
    }

    if (customizations) {
      cartItem.customizations = customizations.map((ci: any) => ({
        posItemId: ci.posItemId,
        label: ci.label,
        quantity:
          ci.modCodeId === 1 ? ci.quantity - ci.defaultQuantity : ci.quantity,
        modCodeId: ci.modCodeId,
      }));
    }
    return cartItem;
  };

  async function onSaveOrder(payload: any) {
    try {
      const { data, error, errorData } = await ApiService.validateOrderAsync(
        payload
      );
      if (data && data.orderId) {
        setErrors(initialErrorState);
        const { data: savedData, error: savedError } =
          await ApiService.saveOrderAsync(data.orderId, latitude, longitude);
        if (savedData) {
          dispatch(clearCart());
          dispatch(setIsPointsUpdate(true));
          showSuccessToast('Order placed successfully.');
          navigate(Routes.OrderSuccessful, {
            state: {
              promiseDatetime: payload.promiseDatetime,
              orderNo: savedData.orderNo,
              orderId: data.orderId,
            },
          });
          setPromiseDateTime(null);
        } else {
          setErrors((prev) => ({ ...prev, cart: savedError }));
        }
      } else if (error) {
        setErrors((prev) => ({
          ...prev,
          cart: error,
          items: errorData ? errorData : [],
        }));
      }
    } finally {
      setLoading(false);
    }
  }

  const calculatePrice = (item: {
    isFree?: any;
    customizations?: string | any[];
    type: any;
    price?: any;
    totalCutomizedItemPrice?: any;
    swappedPrice?: any;
    quantity?: any;
    options?: any;
  }) => {
    if (item.type === Enums.comboType.quickCombo) {
      return utils.formatCurrency(
        (item.price + item.totalCutomizedItemPrice + item.swappedPrice) *
        item.quantity
      );
    } else {
      let itemPrice =
        (item.price + item.totalCutomizedItemPrice) * item.quantity;
      if (item.options && item.options.length > 0) {
        itemPrice += item.options[0].price * item.quantity;
      }
      return utils.formatCurrency(itemPrice);
    }
  };

  const renderCartPrice = (item: any) => {
    if (item.isFree) {
      if (item.customizations && item.customizations.length > 0) {
        return calculatePrice(item);
      }
      return 'Free';
    } else if (item.type === Enums.comboType.quickCombo) {
      return calculatePrice(item);
    } else {
      return calculatePrice(item);
    }
  };

  const renderCustomizeItems = (customizeItems: any) => {
    if (_.isEmpty(customizeItems)) {
      return null;
    }
    if (customizeItems && customizeItems.type === Enums.comboType.menuItem) {
      return (
        <span className='t10' style={{ paddingLeft: 8 }}>
          {customizeItems && customizeItems.customizations && customizeItems.customizations.length ? `${customizeItems.customizations.length} item customized!` : null}</span>
      )
    } else {
      return (
        <span className='t10' style={{ paddingLeft: 8 }}>
          {customizeItems && customizeItems.items[0] && customizeItems.items[0].customizations
            && customizeItems.items[0].customizations.length ? `${customizeItems.items[0].customizations.length} item customized!` : null}</span>
      )
    }
  };

  const handleSwitchToggle = () => {
    setIsSwitchActive(prevIsActive => !prevIsActive);
  };

  const renderPointsView = (): JSX.Element => {
    return (
      <>
        <div style={{
          padding: 20,
          borderRadius: 10,
          marginBottom: 14,
          backgroundColor: 'var(--white-color)',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', padding: '0 16px' }}>
            <span role="img" aria-label="trophy" style={{ fontSize: '24px' }}>⭐</span>
            <h4 style={{ margin: 0, paddingLeft: '8px', flexGrow: 1, fontWeight: 'bold' }}>
              Enjoy using {pointsToBeRedeem} happy stars
            </h4>
            <button
              onClick={handleSwitchToggle}
              style={{
                width: isMobile ? '65px' : '50px',
                height: '25px',
                borderRadius: '12px',
                border: 'none',
                backgroundColor: isSwitchActive ? 'var(--main-turquoise)' : 'var(--text-color)',
                position: 'relative',
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              <span style={{
                position: 'absolute',
                left: isSwitchActive ? '26px' : '4px',
                top: '2.5px',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: 'white',
                transition: 'left 0.3s ease',
              }}></span>
            </button>
          </div>
        </div>
      </>
    );
  };

  const renderHeader = (): JSX.Element => {
    return <components.Header title='Checkout' showGoBack={true} isShowHome={true} />;
  };

  const renderSummary = (): JSX.Element => {
    return (
      <section
        style={{
          padding: 20,
          borderRadius: 10,
          marginTop: 10,
          marginBottom: 14,
          backgroundColor: "var(--white-color)",
          border: '1px solid var(--main-turquoise)',
        }}
      >
        <div
          className='row-center-space-between'
          style={{
            paddingBottom: 20,
            marginBottom: 20,
            borderBottom: '1px solid var(--border-color)',
          }}
        >
          <span
            className='t18'
            style={{ color: 'var(--main-color)', textTransform: 'capitalize' }}
          >
            My order
          </span>
          <span className='t18' style={{ color: 'var(--main-color)' }}>
            ${totalCheckOutPrice.toFixed(2)}
          </span>
        </div>
        <ul>
          {cartItems.map((item) => {
            return (
              <>
                <li
                  key={item._id}
                  className='row-center-space-between'
                  style={{ marginBottom: item.customizations ? 2 : 8 }}
                >
                  <span className='t14' style={{ fontWeight: 500 }}>
                    {item.name}
                  </span>
                  <span className='t14' style={{ fontWeight: 500 }}>
                    {item.quantity} x {renderCartPrice(item)}
                  </span>
                </li>
                <li
                  key={item._id}
                  style={{ marginBottom: 8 }}
                >
                  {renderCustomizeItems(item)}
                </li>
              </>
            );
          })}
        </ul>
        <div className='row-center-space-between' style={{ marginBottom: 8 }}>
          <span className='t12'>GST included</span>
          <span className='t12'>
            {utils.formatCurrency(totalCheckOutPrice / constants.gstAmount)}
          </span>
        </div>
      </section>
    );
  };

  const renderSite = (): JSX.Element => {
    let address;
    if (selectedSite) {
      address = utils.formatAddress(selectedSite);
    }
    return (
      <section
        style={{
          padding: 20,
          borderRadius: 10,
          backgroundColor: 'var(--white-color)',
        }}
        className='row-center-space-between clickable'
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span
            className='t16 number-of-lines-1'
            style={{
              fontWeight: 500,
              marginBottom: 8,
              color: 'var(--main-color)',
              textTransform: 'capitalize',
            }}
          >
            {selectedSite && selectedSite.siteName}
          </span>
          {selectedSite && address && (
            <span className='t12 number-of-lines-1'>
              {address.line1 + address.line2}
            </span>
          )}
        </div>
      </section>
    );
  };

  const renderPaymentMethod = (): JSX.Element => {
    return (
      <>
        {isSwitchActive ? null : (
          <section
            style={{
              padding: 20,
              borderRadius: 10,
              backgroundColor: 'var(--white-color)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
            onClick={() => {
              if (!user?.isGuest) {
                onCheckLogin();
              }
            }}
            className='row-center-space-between clickable'
          >
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
              {paymentMethod && paymentMethod.paymentMethodId && < img
                src={paymentMethod && paymentMethod.brand === "VISA" ? visaCardImg : masterCardImg}
                alt={paymentMethod && paymentMethod.brand}
                style={{
                  width: 40,
                  height: 25,
                  objectFit: "contain",
                  marginRight: 15,
                }}
              />}

              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span
                  className='t14 number-of-lines-1'
                  style={{
                    fontWeight: 500,
                    marginBottom: paymentMethod ? 8 : 0,
                    color: 'var(--main-color)',
                    textTransform: 'capitalize',
                  }}
                >
                  {paymentMethod && paymentMethod.lastFourDigits
                    ? 'Payment Method'
                    : 'Select Payment Method'}
                </span>
                {paymentMethod && paymentMethod.lastFourDigits && (
                  <span className='t12 number-of-lines-1'>
                    **** **** **** {paymentMethod.lastFourDigits}
                  </span>
                )}
              </div>
            </div>
            <svg.RightArrowSvg />
          </section>
        )}
      </>
    );
  };

  const renderFooter = (): JSX.Element => {
    return (
      <section style={{ padding: 20 }}>
        <div style={{ marginBottom: 10, marginLeft: 10 }}>
          {errors.date && (
            <span className='t16' style={{ color: 'red' }}>
              {errors.date}
            </span>
          )}
          {errors.cart && (
            <span className='t16' style={{ color: 'red' }}>
              {errors.cart.cart}
            </span>
          )}
          {errors.site && (
            <span className='t16' style={{ color: 'red' }}>
              {errors.site}
            </span>
          )}
          {errors.breakfast && (
            <span className='t16' style={{ color: 'red' }}>
              {errors.breakfast}
            </span>
          )}
        </div>
        <components.Button
          text='Confirm order'
          onClick={onPressOrderValidate}
        />
      </section>
    );
  };

  const renderContent = (): JSX.Element => {
    return (
      <main className='scrollable container' style={{ top: 15 }}>
        {renderSite()}
        {renderSummary()}
        {totalUserPoints >= pointsToBeRedeem && totalCheckOutPrice > 0 && renderPointsView()}
        {renderPaymentMethod()}
      </main>
    );
  };

  const renderLoader = (): JSX.Element | null => {
    if (!loading) return null;
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.25)',
          zIndex: 9999,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <components.Loader />
      </div>
    );
  };

  return (
    <div id='screen' style={{ opacity }}>
      {renderLoader()}
      {renderHeader()}
      {renderContent()}
      {renderFooter()}
    </div>
  );
};
